/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { ComponentPortal, DomPortalOutlet } from '@angular/cdk/portal';
import {
    ApplicationRef,
    ChangeDetectorRef,
    Component,
    ComponentRef,
    HostListener,
    Injector,
    Input,
    OnDestroy,
    SimpleChange,
    SimpleChanges
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { Store } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { concatMap, filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { AppConfigState } from '../../../../../core/state/appConfig/app-config.state';
import { WindowRefService } from '../../../../../core/ui/services/windowRef.service';
import { ReportContentContainerPrintComponent } from '../../../reportContent/report-content-container-print.component';
import { IsolatedReportService } from '../../../services/isolatedReport.service';
import { ReportDefinition } from '../../../state/report.model';
import { ReportState } from '../../../state/report.state';

@Component({
    selector: 'dash-gis-report-print',
    templateUrl: './report-print.component.html',
    styleUrls: [ './report-print.component.scss' ],
    imports: [ MatIconButton, MatTooltip, MatIcon ]
})
export class ReportPrintComponent implements OnDestroy {
    @Input() definition: ReportDefinition;

    private portalOutlet: DomPortalOutlet;
    private printContent: ComponentRef<ReportContentContainerPrintComponent>;
    private stopSubject = new Subject<void>();
    private stop$: Observable<void>;

    constructor(
        private isolatedReport: IsolatedReportService,
        private appRef: ApplicationRef,
        private injector: Injector,
        private cdr: ChangeDetectorRef,
        private windowRef: WindowRefService,
        private store: Store
    ) {
        this.stop$ = this.stopSubject.asObservable();
    }

    ngOnDestroy() {
        this.stopSubject.next();
        if (this.portalOutlet) {
            this.portalOutlet.detach();
        }
    }

    @HostListener('window:afterprint')
    afterPrint() {
        this.stopSubject.next();
        if (this.portalOutlet) {
            this.portalOutlet.detach();
        }
    }

    print() {
        const printDelay = this.store.selectSnapshot(AppConfigState.printDelay);
        if (this.portalOutlet && this.portalOutlet.hasAttached()) {
            this.portalOutlet.detach();
        }
        this.createPrintPortal();
        this.printContent.instance.initialized$
            .pipe(
                filter((b) => b),
                tap(() => this.isolatedReport.triggerAllData()),
                concatMap(() => this.printContent.instance.dataSource.loading$),
                filter((loading) => !loading),
                concatMap(() => this.printContent.instance.dataSource.reportAllData$),
                filter((data) => data !== null),
                take(1)
            )
            .subscribe(() => {
                setTimeout(() => {
                    this.windowRef.nativeWindow.print();
                }, printDelay);
            });
    }

    createPrintPortal() {
        const portal = new ComponentPortal(ReportContentContainerPrintComponent, null, this.injector);
        this.portalOutlet = new DomPortalOutlet(document.querySelector('#printContent'), this.appRef, this.injector);
        this.printContent = this.portalOutlet.attachComponentPortal<ReportContentContainerPrintComponent>(portal);
        this.keys$.pipe(takeUntil(this.stop$)).subscribe((keys) => {
            const changes: SimpleChanges = {};

            const keysChange = this.buildChange(this.printContent.instance.keys, keys);
            this.printContent.instance.keys = keys;
            changes['keys'] = keysChange;

            const definitionChange = this.buildChange(this.printContent.instance.definition, this.definition);
            this.printContent.instance.definition = this.definition;
            changes['definition'] = definitionChange;

            const printChange = this.buildChange(this.printContent.instance.print, true);
            this.printContent.instance.print = true;
            changes['print'] = printChange;

            this.printContent.instance.ngOnChanges(changes);
            this.cdr.markForCheck();
        });
    }

    private buildChange(previous, current): SimpleChange {
        let firstChange = false;
        if (typeof (previous === 'undefined')) {
            firstChange = true;
        }
        return new SimpleChange(previous, current, firstChange);
    }

    get keys$(): Observable<any[]> {
        return this.store.select(ReportState.featureReportKeys).pipe(map((fn) => fn(this.definition.layer)));
    }
}
